/**
 * Compose two sets of entity service hooks into one.
 *
 * Used to merge hooks declared on an EntityDefinition with hooks
 * passed via CreateEntityServiceOptions. Definition hooks run first,
 * options hooks run second.
 *
 * - before hooks: pipeline (data flows definition → options)
 * - after hooks: sequential (both run, definition first)
 * - find hooks: pipeline (result flows definition → options)
 */

import type { EntityServiceHooks } from '@gzl10/nexus-sdk'

export function composeHooks<T = unknown>(
  first?: EntityServiceHooks<T>,
  second?: EntityServiceHooks<T>
): EntityServiceHooks<T> | undefined {
  if (!first && !second) return undefined
  if (!first) return second
  if (!second) return first

  return {
    beforeCreate: first.beforeCreate || second.beforeCreate
      ? async (data) => {
          let result = data
          if (first.beforeCreate) result = await first.beforeCreate(result)
          if (second.beforeCreate) result = await second.beforeCreate(result)
          return result
        }
      : undefined,

    beforeUpdate: first.beforeUpdate || second.beforeUpdate
      ? async (id, data) => {
          let result = data
          if (first.beforeUpdate) result = await first.beforeUpdate(id, result)
          if (second.beforeUpdate) result = await second.beforeUpdate(id, result)
          return result
        }
      : undefined,

    beforeDelete: first.beforeDelete || second.beforeDelete
      ? async (id) => {
          if (first.beforeDelete) await first.beforeDelete(id)
          if (second.beforeDelete) await second.beforeDelete(id)
        }
      : undefined,

    afterCreate: first.afterCreate || second.afterCreate
      ? async (record) => {
          if (first.afterCreate) await first.afterCreate(record)
          if (second.afterCreate) await second.afterCreate(record)
        }
      : undefined,

    afterUpdate: first.afterUpdate || second.afterUpdate
      ? async (record) => {
          if (first.afterUpdate) await first.afterUpdate(record)
          if (second.afterUpdate) await second.afterUpdate(record)
        }
      : undefined,

    afterDelete: first.afterDelete || second.afterDelete
      ? async (id) => {
          if (first.afterDelete) await first.afterDelete(id)
          if (second.afterDelete) await second.afterDelete(id)
        }
      : undefined,

    afterFindById: first.afterFindById || second.afterFindById
      ? async (record) => {
          let result = record
          if (first.afterFindById) result = await first.afterFindById(result)
          if (second.afterFindById) result = await second.afterFindById(result)
          return result
        }
      : undefined,

    afterFindAll: first.afterFindAll || second.afterFindAll
      ? async (paginatedResult) => {
          let result = paginatedResult
          if (first.afterFindAll) result = await first.afterFindAll(result)
          if (second.afterFindAll) result = await second.afterFindAll(result)
          return result
        }
      : undefined
  }
}
